import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getBillingSubscription, BillingSubscription } from '../services/api.ts';
import { useAuth } from './AuthContext.tsx';

/**
 * Tenant subscription state, fetched once per session and shared app-wide.
 *
 * Its job is to answer one question — `canWrite` — which `AdminOnly` and `ReadOnlyBanner`
 * use to hide or explain mutating controls when a subscription has lapsed.
 *
 * This is a UX affordance ONLY. The authoritative paywall is backend/src/utils/entitlement.ts,
 * which returns 402 regardless of what this context believes. That asymmetry matters here:
 * we deliberately default to `canWrite: true` whenever the status is unknown (still loading,
 * request failed, older backend that omits `readOnly`), because wrongly greying out the whole
 * app over a failed status call would be far worse than letting a click through and receiving
 * an honest 402 from the server.
 */

interface SubscriptionContextValue {
  /** Latest subscription payload, or null before the first successful load. */
  subscription: BillingSubscription | null;
  /** False only when the backend explicitly says this tenant is write-blocked. */
  canWrite: boolean;
  /** True while the first fetch is in flight. */
  loading: boolean;
  /** Re-fetch (e.g. after returning from Stripe Checkout). */
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, customerId } = useAuth();
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // No tenant claim means every tenant-scoped call 403s anyway; don't add noise.
    if (!user || !customerId) return;
    setLoading(true);
    try {
      setSubscription(await getBillingSubscription());
    } catch (e) {
      // Fail open — see the note above.
      console.error('[subscription] status unavailable; assuming write access', e);
    } finally {
      setLoading(false);
    }
  }, [user, customerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      subscription,
      canWrite: subscription?.readOnly !== true,
      loading,
      refresh,
    }),
    [subscription, loading, refresh]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

/**
 * Safe to call outside a SubscriptionProvider — it degrades to "can write", so a component
 * rendered in isolation (or in a test that does not wrap the provider) behaves exactly as it
 * did before the paywall existed.
 */
export const useSubscription = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext);
  return (
    context ?? {
      subscription: null,
      canWrite: true,
      loading: false,
      refresh: async () => {},
    }
  );
};
